import React, { useEffect, useState, useCallback, useRef, useContext } from 'react'

import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom'

import { useSettingsEdit } from './SettingsEditProvider'

// --- Module-level ref handle for external clearing ---

let _guardRef: React.MutableRefObject<boolean> | null = null

export const clearGuardPendingState = () => {
  if (_guardRef) _guardRef.current = false
}

export const useSettingsNavigationGuard = ({
  pathPattern,
}: {
  pathPattern: RegExp
}) => {
  const { hasPendingChanges, saveAll, clearAllChanges } = useSettingsEdit()
  const location = useLocation()
  const { navigator } = useContext(UNSAFE_NavigationContext)

  const [isBlocked, setIsBlocked] = useState(false)

  const pendingNavigationRef = useRef<{
    method: 'push' | 'replace' | 'go'
    args: unknown[]
  } | null>(null)

  const hasPendingChangesRef = useRef(hasPendingChanges)
  hasPendingChangesRef.current = hasPendingChanges
  _guardRef = hasPendingChangesRef
  const locationRef = useRef(location)
  locationRef.current = location

  const isSettingsPath = useCallback(
    (pathname: string) => {
      const match = locationRef.current.pathname.match(pathPattern)
      if (!match) return false
      return pathname.startsWith(match[1])
    },
    [pathPattern]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const originalPush = navigator.push.bind(navigator)
    const originalReplace = navigator.replace.bind(navigator)
    const originalGo = navigator.go.bind(navigator)

    navigator.push = (...args: Parameters<typeof navigator.push>) => {
      const [to] = args
      const pathname = typeof to === 'string' ? to : to.pathname ?? ''
      if (hasPendingChangesRef.current && !isSettingsPath(pathname)) {
        pendingNavigationRef.current = { method: 'push', args }
        setIsBlocked(true)
        return
      }
      originalPush(...args)
    }

    navigator.replace = (...args: Parameters<typeof navigator.replace>) => {
      const [to] = args
      const pathname = typeof to === 'string' ? to : to.pathname ?? ''
      if (hasPendingChangesRef.current && !isSettingsPath(pathname)) {
        pendingNavigationRef.current = { method: 'replace', args }
        setIsBlocked(true)
        return
      }
      originalReplace(...args)
    }

    navigator.go = (...args: Parameters<typeof navigator.go>) => {
      if (hasPendingChangesRef.current) {
        pendingNavigationRef.current = { method: 'go', args }
        setIsBlocked(true)
        return
      }
      originalGo(...args)
    }

    return () => {
      navigator.push = originalPush
      navigator.replace = originalReplace
      navigator.go = originalGo
    }
  }, [navigator, isSettingsPath])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (event: BeforeUnloadEvent) => {
      if (hasPendingChangesRef.current) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  const executePendingNavigation = useCallback(() => {
    const pending = pendingNavigationRef.current
    if (!pending) return
    pendingNavigationRef.current = null
    setIsBlocked(false)
    if (pending.method === 'push') {
      navigator.push(...(pending.args as Parameters<typeof navigator.push>))
    } else if (pending.method === 'replace') {
      navigator.replace(
        ...(pending.args as Parameters<typeof navigator.replace>)
      )
    } else if (pending.method === 'go') {
      navigator.go(...(pending.args as Parameters<typeof navigator.go>))
    }
  }, [navigator])

  const handleSaveAndLeave = useCallback(async () => {
    await saveAll()
    hasPendingChangesRef.current = false
    executePendingNavigation()
  }, [saveAll, executePendingNavigation])

  const handleDiscardAndLeave = useCallback(() => {
    clearAllChanges()
    hasPendingChangesRef.current = false
    executePendingNavigation()
  }, [clearAllChanges, executePendingNavigation])

  const handleCancel = useCallback(() => {
    pendingNavigationRef.current = null
    setIsBlocked(false)
  }, [])

  return {
    isBlocked,
    handleSaveAndLeave,
    handleDiscardAndLeave,
    handleCancel,
  }
}
